import { DocumentGroup, caseNotesApi, documentsApi } from "@/api";
import { withLocalErrorHandling } from "@/api/middleware/errorMiddleware";
import {
  fetchClientDocumentBlob,
  fetchClientDocumentPreviewBlob,
} from "@/common/components/layout/components/dropzone/hooks/dropzoneFetch.utils";
import { useDropzoneSection } from "@/common/components/layout/components/dropzone/hooks/useDropzoneSection";
import { getLocalTimeZoneId } from "@/common/utils/getLocalTimeZoneId";
import { QueryKey } from "@/query/Query";

export function useClientSelfUploads(clientId: string | undefined) {
  return useDropzoneSection({
    zone: DocumentGroup.ClientSelf,
    queryKey: [QueryKey.ClientSelfDocuments, clientId],
    queryFn: async () => {
      if (!clientId) {
        throw new Error("Client ID is required");
      }
      return documentsApi.getDocumentsFromClientSelfDropZone({ clientId });
    },
    deleteFn: (files) => {
      if (!clientId) {
        throw new Error("Client ID is required");
      }
      return documentsApi.deleteDocuments({ clientId, ids: files.map((f) => f.documentId) });
    },
    fetchDownloadBlobFn: (file) => {
      if (!clientId) {
        throw new Error("Client ID is required");
      }
      return fetchClientDocumentBlob(clientId, file.documentId);
    },
    fetchPreviewBlobFn: (file) => {
      if (!clientId) {
        throw new Error("Client ID is required");
      }
      return fetchClientDocumentPreviewBlob(clientId, file.documentId);
    },
    renameFn: (file, newFileName) => {
      if (!clientId) {
        throw new Error("Client ID is required");
      }
      return documentsApi.renameDocument({
        clientId,
        documentId: file.documentId,
        renameDocumentRequest: { newFileName },
      });
    },
    createCaseNoteFn: (file) => {
      if (!clientId) {
        throw new Error("Client ID is required");
      }
      return caseNotesApi.createCaseNoteFromDocument({
        clientId,
        createCaseNoteFromDocumentRequest: {
          source: DocumentGroup.ClientSelf,
          fileId: file.documentId,
          originTimeZoneId: getLocalTimeZoneId(),
        },
      });
    },
    convertToPdfFn: (file) => {
      if (!clientId) {
        throw new Error("Client ID is required");
      }
      return documentsApi.convertDocumentToPdf({ clientId, documentId: file.documentId }, withLocalErrorHandling);
    },
    invalidationKeys: [[QueryKey.ClientSelfDocuments, clientId]],
    caseNoteInvalidationKeys: [[QueryKey.ClientCaseNotes, clientId]],
    uploadingFiles: [],
    enabled: !!clientId,
  });
}
